<script setup>
import Jumbo from '@/components/Docs/Jumbo.vue'
import DocSection from '@/components/Docs/Section.vue'
import CodeBlock from '@/components/Docs/CodeBlock.vue'
import JsonBlock from '@/components/Docs/JsonBlock.vue'
  
</script>

<template>
  <div class="wrap row">
    <jumbo title="URL">
    A URL (Uniform Resource Locator) is a web address that specifies the location of a resource on the internet. It serves as a unique identifier for accessing web pages, files, or other resources hosted on web servers. A typical URL consists of several components, including the protocol (such as HTTP or HTTPS), the domain name or IP address of the server hosting the resource, the path to the specific file or directory, and optional query parameters.<br><br> 
With webscout it is possible to visit, scan, and profile a website without having to navigate to it and possibly endangering yourself.
    </jumbo>


    <doc-section title="Start URL scan">
      <template v-slot:left>
Depending on the availability of the URL, scans may take 5-30 seconds. Therefore the starting, monitoring, and getting of results are separated into standalone endpoints. 
<br><br>
To start a scan, send a GET request to /query/url with the ?url query parameter being a valid http(s) URL or a fully qualified domain name. If the url has been scanned recently it will return immediately with a status of “done”. Otherwise you will usually find yourself placed in a queue. 
<br><br>
The Id of the scan is used for checking the status, and retrieving the results.

      </template>

      <template v-slot:right>
        <code-block title="/query/url?url=&lt;URL&gt;" method="GET">
          curl -X GET "https://api.webscout.io/query/url?url=&lt;HTTP_URL | FQDN&gt;"
        </code-block>

        <json-block title="Response">
{"Status":"done | queued | failed","Position":5,"Data":{"Cache":false,"Url":"https://lavadesign.us/","Id":"29b79386-45bb-48be-beff-5dcc9bdfd1da"}}
        </json-block>
      </template>
    </doc-section>


    <doc-section title="Get status of scan">
      <template v-slot:left>
When checking the status of a scan, the status in the response will remain “queued” until it has finished. The position indicates your position in the queue. If it has a value of 0, it means your scan has been picked up by one of our scanning nodes, and results will usually be ready within 30 seconds from the point.
      </template>

      <template v-slot:right>
        <code-block title="/query/url/status"  method="GET">
          curl -X GET "https://api.webscout.io/query/url/status?url=&lt;SCAN_ID&gt;"
        </code-block>

        <json-block title="Response">
{"Status":"queued","Position":0,"Data":null}

        </json-block>
      </template>
    </doc-section>

    <doc-section title="Get result of scan">
      <template v-slot:left>
        Once the scan has finished, you can use the scan id to retrieve the entire result. <br><br>
Within the data segment, there is another data segment. This part contains the information extracted from the page. Note the structure of the LeadRequests and Requests objects. These are structured as arrays. If they have a length longer than 1 it is an indication of redirects, and each element in the array contains the information from that redirect - in the order that they happened. The first element in the array will be the initial request, and the last be where it terminated. 
<br><br>
The LeadRequest is the initial navigation to the page, and the Requests are “usually” the resources loaded as part of serving it.
      </template>

      <template v-slot:right>
        <code-block title="/query/url/result"  method="GET">
          curl -X GET "https://api.webscout.io/query/url/result?url=&lt;SCAN_ID&gt;"
        </code-block>

        <json-block title="Response">
{
  "Status": "ok",
  "Position": 0,
  "Data": {
    "Streams": [
      {
        "Featured": false,
        "Name": "montysecurity Ares RAT C2 IPs"
      },
      {
        "Featured": false,
        "Name": "montysecurity Poison Ivy Trojan IPs"
      },
      {
        "Featured": false,
        "Name": "DataPlane SIP OPTIONS tracker"
      }
    ],
    "Articles": [],
    "Data": {
      "Id": "2a8a1652-40de-4281-a617-8f84e565c401",
      "Url": "http://www.icann.org/epp",
      "FinalUrl": "https://www.icann.org/resources/pages/epp-status-codes-2014-06-16-en",
      "Ip": "192.0.32.7",
      "StatusCode": 200,
      "Jarm": "3fd3fd0003fd3fd00042d42d00042d01a05e5e7e28522d5dc83e0500c983cf",
      "LeadRequest": {
        "Requests": [
          {
            "Url": "http://www.icann.org/epp",
            "Domain": "www.icann.org",
            "File": "epp",
            "IP": {
              "IP": "192.0.32.7",
              "Country": "United States",
              "CountryIso": "US"
            },
            "Method": "GET",
            "Port": 80,
            "IsHttps": false,
            "Size": 0,
            "MimeType": "",
            "StatusCode": 302,
            "HttpFingerprint": {
              "fingerprint": "a6b6edef7c973b15756022cbd2b6905190d2be7463d1d46ee65604ff886bcf17",
              "server": "Apache",
              "powered_by": "",
              "cookies": null,
              "ContentType": ""
            },
            "PayloadSha256": "",
            "PayloadSimhash": 0,
            "PayloadPhash": 0,
            "Jarm": "",
            "CertificateSubject": "",
            "CertificateAlternateNames": null,
            "CertificateValidTo": "0001-01-01T00:00:00Z",
            "CertificateIssuer": "",
            "CPE": null,
            "Artifacts": null
          },
          {
            "Url": "https://www.icann.org/epp",
            "Domain": "www.icann.org",
            "File": "epp",
            "IP": {
              "IP": "192.0.32.7",
              "Country": "United States",
              "CountryIso": "US"
            },
            "Method": "GET",
            "Port": 443,
            "IsHttps": true,
            "Size": 0,
            "MimeType": "",
            "StatusCode": 301,
            "HttpFingerprint": {
              "fingerprint": "ffd62f13bd9be3e21de6efe4d9038c5710904e1c563b227e69a20f8f337ee9d8",
              "server": "Apache",
              "powered_by": "",
              "cookies": null,
              "ContentType": ""
            },
            "PayloadSha256": "",
            "PayloadSimhash": 0,
            "PayloadPhash": 0,
            "Jarm": "",
            "CertificateSubject": "*.icann.org",
            "CertificateAlternateNames": [
              "*.icann.org",
              "icann.org"
            ],
            "CertificateValidTo": "2025-01-03T23:59:59Z",
            "CertificateIssuer": "Sectigo RSA Organization Validation Secure Server CA",
            "CPE": null,
            "Artifacts": null
          },
          {
            "Url": "https://www.icann.org/resources/pages/epp-status-codes-2014-06-16-en",
            "Domain": "www.icann.org",
            "File": "epp-status-codes-2014-06-16-en",
            "IP": {
              "IP": "192.0.32.7",
              "Country": "United States",
              "CountryIso": "US"
            },
            "Method": "GET",
            "Port": 443,
            "IsHttps": true,
            "Size": 0,
            "MimeType": "text/html",
            "StatusCode": 200,
            "HttpFingerprint": {
              "fingerprint": "89c43b9b8eddc5cd6ca14bb7654ad895087c10b859d0b64220027a386e3a836c",
              "server": "Apache",
              "powered_by": "",
              "cookies": null,
              "ContentType": "text/html; charset=utf-8"
            },
            "PayloadSha256": "",
            "PayloadSimhash": 0,
            "PayloadPhash": 0,
            "Jarm": "",
            "CertificateSubject": "*.icann.org",
            "CertificateAlternateNames": [
              "*.icann.org",
              "icann.org"
            ],
            "CertificateValidTo": "2025-01-03T23:59:59Z",
            "CertificateIssuer": "Sectigo RSA Organization Validation Secure Server CA",
            "CPE": null,
            "Artifacts": null
          }
        ],
        "MimeType": "text/html",
        "StatusCode": 200
      },
      "Requests": [
        {
          "Requests": [
            {
              "Url": "https://www.icann.org/assets/fontawesome-webfont-aadc3580d2b64ff5a7e6f1425587db4e8b033efcbf8f5c332ca52a5ed580c87c.woff2?v=4.3.0",
              "Domain": "www.icann.org",
              "File": "fontawesome-webfont-aadc3580d2b64ff5a7e6f1425587db4e8b033efcbf8f5c332ca52a5ed580c87c.woff2",
              "IP": {
                "IP": "192.0.32.7",
                "Country": "United States",
                "CountryIso": "US"
              },
              "Method": "GET",
              "Port": 443,
              "IsHttps": true,
              "Size": 39782,
              "MimeType": "application/font-woff2",
              "StatusCode": 200,
              "HttpFingerprint": {
                "fingerprint": "cd9135a09876774de09e5abd3e18d75079d19167a5c7c062816e2c7c07141d66",
                "server": "Apache",
                "powered_by": "",
                "cookies": null,
                "ContentType": "application/font-woff2"
              },
              "PayloadSha256": "91e7a9beea690d59dded319db2e8ea5e3faa83eafd10cea6b035a4fc24bd28b3",
              "PayloadSimhash": 0,
              "PayloadPhash": 0,
              "Jarm": "",
              "CertificateSubject": "*.icann.org",
              "CertificateAlternateNames": [
                "*.icann.org",
                "icann.org"
              ],
              "CertificateValidTo": "2025-01-03T23:59:59Z",
              "CertificateIssuer": "Sectigo RSA Organization Validation Secure Server CA",
              "CPE": null,
              "Artifacts": null
            }
          ],
          "MimeType": "application/font-woff2",
          "StatusCode": 200
        }
      ],
      "Other": null,
      "Iframes": null,
      "Cookies": [
        {
          "name": "_new_icann_session",
          "value": "00757de762b29d3f041ab1da7269c0ef",
          "domain": "www.icann.org",
          "path": "/",
          "expires": -1,
          "size": 50,
          "httpOnly": true,
          "secure": true,
          "session": true,
          "priority": "Medium",
          "sameParty": false,
          "sourceScheme": "Secure",
          "sourcePort": 443
        }
      ],
      "IsHttps": true,
      "MimeType": "text/html",
      "Screenshot": {
        "Url": "seeker/scans/2a8a1652-40de-4281-a617-8f84e565c401/screenshot.png",
        "Phash": 13480986956673726000,
        "Sha256": "9cd6b939c9a8ed18de7f6c92ae5dbcbf3a7b69993a2e97a2d8aac65b0a8de3e2"
      },
      "Favicon": {
        "Url": "seeker/scans/2a8a1652-40de-4281-a617-8f84e565c401/favicon.ico",
        "OriginUrl": "https://www.icann.org/favicon.ico",
        "Phash": 0,
        "Sha256": "b62940e25a7529cc5e3d313724ec0f10d5889f81a4aaf9a2d6446879c02763f2"
      },
      "Content": {
        "Url": "seeker/scans/2a8a1652-40de-4281-a617-8f84e565c401/content",
        "Simhash": 9814453126613903000,
        "Sha256": "9dc38e6b0d3bb81682dc4cbf677c54b90055bc6339122672dbff4726cbd0e62b",
        "Elements": {
          "title": "EPP Status Codes | What Do They Mean, and Why Should I Know? - ICANN",
          "body_hash": "701cba55d43d43e6f7d63d9b7fab2166fb01a38907a0bcf2796c74c69f4760c8",
          "head_hash": "353e3bfeb30932a5ee5baf1c79d174b8cf036a9a8dce0c96d2dd116a12307c71",
          "content_simhash": 9804973137818500000,
          "content_hash": "4a12ddeb6dd439abe57f58bf7725c4945ab11c36810054464ac16b6236bfbbe3",
          "links": [
            {
              "type": "href",
              "url": "/privacy/cookies"
            },
            {
              "type": "href",
              "url": "/privacy"
            }
          ],
          "resources": [
            {
              "type": "link",
              "Local": false,
              "url": "/favicon.ico",
              "hash": "",
              "simhash": 0
            },
            {
              "type": "stylesheet",
              "Local": false,
              "url": "/assets/application-322cd44d84fa96c1edf1da4ff1391cd1dd69c37da6d03f3cb81d347d3097ef49.css",
              "hash": "",
              "simhash": 0
            },
            {
              "type": "stylesheet",
              "Local": false,
              "url": "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css",
              "hash": "",
              "simhash": 0
            },
            {
              "type": "stylesheet",
              "Local": false,
              "url": "/assets/application-322cd44d84fa96c1edf1da4ff1391cd1dd69c37da6d03f3cb81d347d3097ef49.css",
              "hash": "",
              "simhash": 0
            },
            {
              "type": "image",
              "Local": false,
              "url": "/assets/icann_logo-060b2be98ed365541773152e2d7bb0ce5777c299d04fce4d89a14bc8fbc2a14a.png",
              "hash": "",
              "simhash": 0
            },
            {
              "type": "script",
              "Local": false,
              "url": "/assets/application-5f0f875d372bf023d4be678fb2077d9cfb04eccf4f504e85f95802e7e8c880df.js",
              "hash": "",
              "simhash": 0
            },
            {
              "type": "script",
              "Local": false,
              "url": "/glossary_terms.js",
              "hash": "",
              "simhash": 0
            },
            {
              "type": "script",
              "Local": false,
              "url": "/assets/resource_pages-3965701e3ad068e958b0be2937fb75169124fa2a4af3d170fc215bc0967d32b4.js",
              "hash": "",
              "simhash": 0
            }
          ],
          "local_resource": [
            {
              "type": "script",
              "Local": true,
              "url": "local",
              "hash": "e6aa959896a0b7f3bf9eaba44c05962aadf2591a9e5bebb80d32c9dc1be3449b",
              "simhash": 9813998066779896000
            },
            {
              "type": "script",
              "Local": true,
              "url": "local",
              "hash": "8592297a093e02dbe15be758f3e1e038b033fcb6b5edeeb12071e0cb600e7908",
              "simhash": 16835005161703580000
            }
          ],
          "comments": [
            {
              "Text": "HTML5 shim, for IE6-8 support of HTML5 elements",
              "Sha256": "187fdf3f538587da497bdd792d551a98fd4b3ae505423275c8b362eb89b32b45",
              "Simhash": 17965968362196510000
            }
          ]
        },
        "Artifacts": {
          "Apikeys": {},
          "Emails": {},
          "Hashes": {},
          "Network": {
            "domain": [],
            "url": []
          },
          "SocialMedia": {
            "facebook": [
              "icannorg"
            ],
            "twitter": [
              "icann"
            ]
          },
          "Trackers": {}
        },
        "Technologies": null
      },
      "ThirdParty": [
        {
          "Flag": false,
          "Vendor": "Google Webrisk",
          "Data": {
            "expireTime": "0001-01-01T00:00:00Z",
            "threatTypes": null
          }
        }
      ],
      "Catphish": {
        "Malicious": false,
        "class_prediction": "benign",
        "prediction_probability": 13.809573858371479
      },
      "Timestamp": "2024-03-14T14:30:45.286696294Z"
    }
  }
}
        </json-block>
      </template>
    </doc-section>


  </div>
</template>

<style scoped>
.wrap {
  height: 100%;
  overflow-y: scroll;
}

.wrap > * {
  margin-top: 2.5rem;
}
.wrap > *:not(:first-child) {
  border-top: 2px solid #1e252f;
  padding-top: 2.5rem;
}
</style>
